import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useCustomerBasePaths } from '../../utils/customerBasePaths';
import { formatDateTimeSaudi } from '../../utils/saudiTime';
import ShopPhotoPicker from '../../components/ShopPhotoPicker';

export default function CustomersList() {
  const { canEdit } = useAuth();
  const { create, isOrderReceiver } = useCustomerBasePaths();
  const [deletingId, setDeletingId] = useState(null);
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    shopPic: '',
  });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const delay = q.trim() ? 300 : 0;
    const t = setTimeout(async () => {
      setError('');
      setLoading(true);
      try {
        const res = await apiService.customers.list({ q: q.trim() || undefined });
        if (!cancelled) setCustomers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load customers');
          setCustomers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      name: c.name || '',
      phone: c.phone || '',
      shopPic: c.shopPic || '',
    });
    setEditError('');
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setEditError('');
    if (!editForm.name.trim()) {
      setEditError('Name is required.');
      return;
    }
    if (!editForm.phone.trim()) {
      setEditError('Mobile number is required.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        shopPic: editForm.shopPic || '',
      };
      await apiService.customers.update(editing._id, body);
      closeEdit();
      const res = await apiService.customers.list({ q: q.trim() || undefined });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setEditError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!canEdit()) return;
    const label = [c.customerNo, c.name, c.phone].filter(Boolean).join(' · ') || 'this customer';
    if (
      !window.confirm(
        `Delete ${label}?\n\nThis cannot be undone.`
      )
    ) {
      return;
    }
    const id = c._id;
    setError('');
    setDeletingId(String(id));
    try {
      await apiService.customers.remove(id);
      if (editing && String(editing._id) === String(id)) {
        closeEdit();
      }
      const res = await apiService.customers.list({ q: q.trim() || undefined });
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete customer');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700">Customers</h1>
            <p className="text-sm text-gray-600 mt-1">
              {isOrderReceiver
                ? 'View and edit customers. Each save records who made the change.'
                : 'Customer numbers (BSCUST-####) are auto-assigned. Use these records when you place orders from the menu.'}
            </p>
          </div>
          <Link
            to={create}
            className="inline-flex justify-center items-center bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-medium min-h-[44px]"
          >
            Add customer
          </Link>
        </div>
        <div className="mb-4">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer no, name, or mobile…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-700 focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">{error}</div>
        )}
        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading…</div>
        ) : customers.length === 0 ? (
          <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">No customers found.</div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-700 font-semibold">
                <tr>
                  <th className="px-3 py-2">Customer No</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Mobile</th>
                  <th className="px-3 py-2">Shop pic</th>
                  <th className="px-3 py-2 w-32 text-right sm:text-left"> </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c._id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-gray-800">
                      {c.customerNo || '—'}
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800">{c.name}</td>
                    <td className="px-3 py-2 text-gray-700">{c.phone}</td>
                    <td className="px-3 py-2">
                      {c.shopPic ? (
                        <img
                          src={c.shopPic}
                          alt=""
                          className="h-12 w-12 rounded object-cover border border-gray-200"
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end sm:justify-start gap-2">
                        <button
                          type="button"
                          onClick={() => openEdit(c)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          Edit
                        </button>
                        {canEdit() && (
                          <button
                            type="button"
                            onClick={() => handleDelete(c)}
                            disabled={deletingId === String(c._id)}
                            className="text-red-600 hover:text-red-800 font-medium text-sm disabled:opacity-50"
                          >
                            {deletingId === String(c._id) ? 'Deleting…' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          onClick={() => !saving && closeEdit()}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-800 mb-1">Edit customer</h2>
            {editing.lastEditedByName && (
              <p className="text-xs text-gray-500 mb-3">
                Last edited by <span className="font-medium text-gray-700">{editing.lastEditedByName}</span>
                {editing.updatedAt && <> · {formatDateTimeSaudi(editing.updatedAt)}</>}
              </p>
            )}
            <form onSubmit={saveEdit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer No</label>
                <p className="text-gray-800 font-mono">{editing.customerNo || '—'}</p>
                <p className="text-xs text-gray-500 mt-0.5">Cannot be changed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile No *</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700"
                />
              </div>
              <ShopPhotoPicker
                label="Shop pic"
                previewUrl={editForm.shopPic}
                onChange={(url) => setEditForm((f) => ({ ...f, shopPic: url }))}
                onError={setEditError}
                disabled={saving}
              />
              {editError && <div className="p-2 bg-red-100 text-red-700 text-sm rounded">{editError}</div>}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving || deletingId}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-medium"
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={saving || Boolean(deletingId)}
                  onClick={closeEdit}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
              {editing && canEdit() && (
                <div className="pt-2 border-t border-gray-200 mt-2">
                  <button
                    type="button"
                    disabled={saving || deletingId === String(editing._id)}
                    onClick={() => handleDelete(editing)}
                    className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                  >
                    {deletingId === String(editing._id) ? 'Deleting…' : 'Delete customer'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
